<template>
  <v-card>
    <div>
      <div class="d-flex align-center pl-3 pr-3 py-3 header">
        <v-btn
          icon
          @click="$emit('close')"
          class="mr-3"
        >
          <v-icon size="40">mdi-close</v-icon>
        </v-btn>
        <div>
          <h3>{{ mode }} In-app Alert <span class="body-1 ml-2">| {{selectedApp.name}}</span></h3>
        </div>
        <v-spacer />
        <v-btn
          text
          @click="update"
          width="130px"
        >
          {{ mode }}
        </v-btn>
      </div>

      <!-- content -->
      <div class="pb-6">
        <v-form ref="form" v-model="isValid">
          <div class="pa-10 pb-6">
            <v-row>
              <v-col>
                <div class="d-flex align-center mb-3">
                  <div class="mr-6">
                    <span class="title">Message</span>
                  </div>
                </div>
              </v-col>
            </v-row>
            <div>
              <v-text-field
                outlined
                v-model="title"
                label="Title"
                placeholder=" "
                class="mb-3"
                :rules="[rules.required]"
              >
              </v-text-field>
            </div>
            <div>
              <v-text-field
                outlined
                v-model="body"
                label="Body"
                placeholder=" "
                :rules="[rules.required]"
              >
              </v-text-field>
            </div>
          </div>

          <v-divider />

          <div class="pa-10 pb-0">
            <div class="mb-3">
              <span class="title">Set duration</span>
            </div>
            <v-row class="pb-3">
              <v-col 
                cols="6" 
                md="3" 
                class="pr-3"
              >
                <div class="mt-6">
                  <DatePicker 
                    v-model="startDate"
                    label="Start date"
                    :rules="[rules.required]"
                    @input="triggerValidation"
                  />
                </div>
              </v-col>
              <v-col 
                cols="6" 
                md="3" 
              >
                <div class="mt-6">
                  <DatePicker
                    v-model="endDate"
                    label="End date"
                    ref="endDatePicker"
                    :rules="[rules.required, rules.endAfterStartDate]"
                    @input="triggerValidation"
                  />
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="6"
                md="3"
                class="pr-3"
              >
                <div>
                  <TimePicker 
                    v-model="startTime"
                    label="Start time"
                    :rules="[rules.required]"
                    @input="triggerValidation"
                  />
                </div>
              </v-col>
              <v-col
                cols="6"
                md="3"
                class="pr-3"
              >
                <div>
                  <TimePicker 
                    v-model="endTime"
                    label="End time"
                    ref="endTimePicker"
                    :rules="[rules.required, rules.endTimeAfterStartTime]"
                    @input="triggerValidation"
                  />
                </div>
              </v-col>
            </v-row>
          </div>

        </v-form>
        <div class="d-flex justify-end mr-12">
          <v-btn 
            text
            class="mr-6"
            @click="$emit('close')"
          >Cancel</v-btn>
          <v-btn 
            class="primary"
            width="120px"
            @click="update"
            :disabled="!isValid"
          >{{ mode }}</v-btn>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import DatePicker from '@/components/DatePicker';
import TimePicker from '@/components/TimePicker';

const defaultStart = new Date();
const defaultEnd = new Date();
defaultEnd.setMonth(defaultStart.getMonth() + 1);

const initAlert = {
  "id": null,
  "appName": "StaffPortalApp",
  "title": "",
  "body": "",
  "startDatetime": defaultStart.toISOString(),
  "finishDatetime": defaultEnd.toISOString()
};

export default {
  name: 'UpdateAppAlertDialog',
  components: {
    DatePicker,
    TimePicker
  },
  props: {
    value: {
      type: Object,
      default: function() {
        return {};
      }
    }
  },
  data() {
    const apps = [{code: 'StaffPortalApp', name: "Staff Portal App"}];
    const values = this.value || initAlert;
    return {
      apps: apps,
      isValid: false,

      appCode: values.appName,

      id: values.id,
      title: values.title,
      body: values.body,
      startDate: values.startDatetime,
      endDate: values.finishDatetime,
      startTime: values.startDatetime,
      endTime: values.finishDatetime,

      rules: {
        required: val => val != null && val.trim() !== '' || 'Required.',
        versionNumber: val => /^[0-9].*[a-zA-Z0-9]$/.test(val) || 'Version must start with a number and end with an alphanumeric.',
        url: val => /^http[s]?:\/\/.*\..*[a-zA-Z0-9/]$/.test(val) || 'Link must be a url.',
        endAfterStartDate: () => {
          return this.endDate >= this.startDate || 'End date cannot be before Start date.';
        },
        endTimeAfterStartTime: () => {
          let formattedStartTime = '';
          if (this.startTime) {
            formattedStartTime = this.startTime.split(":")
              .map(t => t.toString().padStart(2, '0'))
              .join(':');
          }
          
          if (this.endDate === this.startDate) {
            return this.endTime >= formattedStartTime || 'End date & time must be after Start date & time.'
          }
          return true;
        },
      }
    }
  },
  computed: {
    mode() {
      return this.id == null ? 'Create' : 'Update'
    },
    isUpdate() {
      return this.mode === 'Update';
    },
    selectedApp() {
      return this.$store.getters.selectedApp;
    }
  },
  methods: {
    formatIsoDateTime(date, time) {
      // api wants the date in this ISO/UTC format: "2020-09-01T05:15:49+00:00"
      const formattedTime = time.split(":").map(t => t.toString().padStart(2, '0')).join(":");
      const isoDateStr = new Date(date + "T" + formattedTime).toISOString();
      return isoDateStr.split('.')[0] + "+00:00";
    },
    update() {
      this.triggerValidation();
      if (this.isValid) {
        const startDatetime = this.formatIsoDateTime(this.startDate, this.startTime);
        const finishDatetime = this.formatIsoDateTime(this.endDate, this.endTime);
        const data = {
          id: this.id,
          appCode: this.appCode,
          title: this.title,
          body: this.body,
          startDateTime: startDatetime,
          finishDateTime: finishDatetime,
        };

        this.$store.dispatch('updateInAppAlert', data)
        .then(() => {
          this.$emit('close');
        });
      }
    },
    triggerValidation() {
      if (this.$refs.form) {
        return this.$refs.form.validate();
      }
    },
  }
}
</script>

<style scoped lang="scss">
  .header {
    background-color: $ads-navy;
    color: $ads-white;
    .v-btn {
      color: $ads-white;
    }
  }

  ::v-deep .v-radio:not(.v-radio--is-disabled) label.v-label {
    color: $ads-navy;
    i {
      color: $ads-navy
    }
  }
</style>